import type { CSSProperties } from 'react'

import { Autoplay, EffectFade } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/css'
import 'swiper/css/effect-fade'

import type { StoreBanner } from '@/types/digitalProduct'

const HIGHLIGHT_HEIGHT_CLASS = 'h-[100px]'

type StoreClosesBannerProps = {
  banners: StoreBanner[]
}

const halftoneCornerStyle = (
  origin: 'left' | 'right',
  dotColor: string
): CSSProperties => ({
  backgroundImage: `radial-gradient(circle, ${dotColor} 1px, transparent 1px)`,
  backgroundSize: '5px 5px',
  maskImage: `radial-gradient(ellipse 100% 100% at ${origin} 100%, black 0%, transparent 72%)`,
  WebkitMaskImage: `radial-gradient(ellipse 100% 100% at ${origin} 100%, black 0%, transparent 72%)`
})

const BANNER_THEME = {
  sectionShadow:
    'shadow-[inset_0_-1px_0_rgba(255,255,255,0.18),0_2px_0_rgba(26,0,77,0.35)]',
  backdrop: {
    backgroundColor: 'var(--color-store-banner-surface)',
    backgroundImage: [
      'repeating-conic-gradient(from -18deg at 30% 52%, rgba(255, 255, 255, 0.08) 0deg 4deg, transparent 4deg 14deg)',
      'radial-gradient(ellipse 88% 115% at 32% 50%, rgba(186, 148, 255, 0.42) 0%, transparent 58%)',
      'radial-gradient(ellipse 130% 100% at 50% 50%, transparent 28%, rgba(20, 0, 60, 0.52) 100%)'
    ].join(', ')
  } satisfies CSSProperties,
  halftoneDotColor: 'rgba(0, 0, 0, 0.42)',
  halftoneOpacity: 'opacity-75',
  insetBorder: 'shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)]',
  bottomEdge: 'bg-gradient-to-r from-transparent via-white/30 to-transparent'
}

function StoreClosesBannerBackdrop() {
  const styles = BANNER_THEME

  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={styles.backdrop}
      />

      <div
        aria-hidden
        className={`pointer-events-none absolute bottom-0 left-0 h-2/3 w-2/5 ${styles.halftoneOpacity}`}
        style={halftoneCornerStyle('left', styles.halftoneDotColor)}
      />

      <div
        aria-hidden
        className={`pointer-events-none absolute bottom-0 right-0 h-2/3 w-2/5 ${styles.halftoneOpacity}`}
        style={halftoneCornerStyle('right', styles.halftoneDotColor)}
      />

      <div
        aria-hidden
        className={`pointer-events-none absolute inset-0 rounded-lg ${styles.insetBorder}`}
      />

      <div
        aria-hidden
        className={`pointer-events-none absolute inset-x-0 bottom-0 h-px ${styles.bottomEdge}`}
      />
    </>
  )
}

export function StoreClosesBanner({ banners }: StoreClosesBannerProps) {
  if (!banners.length) return null

  const shouldAutoplay = banners.length > 1

  return (
    <section
      aria-label="Avisos da lojinha"
      className={`relative mb-3 overflow-hidden rounded-lg ${BANNER_THEME.sectionShadow} ${HIGHLIGHT_HEIGHT_CLASS}`}
    >
      <StoreClosesBannerBackdrop />

      <Swiper
        modules={[EffectFade, Autoplay]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        slidesPerView={1}
        loop={shouldAutoplay}
        autoplay={
          shouldAutoplay ? { delay: 7000, disableOnInteraction: false } : false
        }
        className={`relative z-10 w-full ${HIGHLIGHT_HEIGHT_CLASS}`}
      >
        {banners.map(banner => (
          <SwiperSlide
            key={banner.id}
            className={`flex items-center justify-center px-2 ${HIGHLIGHT_HEIGHT_CLASS}`}
          >
            <img
              src={banner.image_url ?? undefined}
              alt={banner.alt_text}
              className="relative z-10 block max-h-full w-full object-contain object-center"
              loading="lazy"
              decoding="async"
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  )
}
